import React, { useState, useEffect } from 'react';
import { 
  Search, TrendingUp, MapPin, AlertCircle, RefreshCw, Download,
  Users, Clock, Filter, ChevronDown, ExternalLink, Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchInsightsDashboard = ({ adminToken }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [recentSearches, setRecentSearches] = useState(null);
  const [insights, setInsights] = useState(null);
  const [timeRange, setTimeRange] = useState(7);
  const [newVisitorsOnly, setNewVisitorsOnly] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [activeTab, setActiveTab] = useState('recent');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [recentRes, insightsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/searches/recent?admin_token=${adminToken}&days=${timeRange}&new_visitors_only=${newVisitorsOnly}&limit=200`),
        axios.get(`${API_URL}/admin/searches/insights?admin_token=${adminToken}&days=${timeRange}`)
      ]);
      
      setRecentSearches(recentRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      toast.error('Failed to load search data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      fetchData();
    }
  }, [adminToken, timeRange, newVisitorsOnly]);

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/searches/export?admin_token=${adminToken}&days=${timeRange}&format=csv`);
      
      // Create and download file
      const blob = new Blob([response.data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.total_records} records`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  // Filter recent searches by search query
  const filteredSearches = recentSearches?.events?.filter(e => 
    !searchFilter || e.search_query?.toLowerCase().includes(searchFilter.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={newVisitorsOnly}
              onChange={(e) => setNewVisitorsOnly(e.target.checked)}
              className="w-4 h-4 rounded border-stone-300"
            />
            <span className="text-sm text-stone-600">New visitors only</span>
          </label>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Search className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-stone-500">Total Searches</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{recentSearches?.summary?.total_searches || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-green-500" />
            <span className="text-xs text-stone-500">New Visitor Searches</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{recentSearches?.summary?.new_visitor_searches || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-stone-500">Zero Results</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{recentSearches?.summary?.zero_results_searches || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-stone-500">Last Updated</span>
          </div>
          <p className="text-sm font-medium text-stone-700">
            {recentSearches?.last_updated ? new Date(recentSearches.last_updated).toLocaleTimeString() : 'N/A'}
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-stone-200">
        {[
          { id: 'recent', label: 'Recent Searches', icon: Clock },
          { id: 'insights', label: 'New User Insights', icon: Zap },
          { id: 'trends', label: 'Trends & Coverage', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id 
                ? 'border-stone-900 text-stone-900' 
                : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Recent Searches Tab */}
      {activeTab === 'recent' && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 bg-stone-50">
            <Input
              placeholder="Search within queries..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="max-w-xs h-9"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Time</th>
                  <th className="px-4 py-3 text-left font-medium">Query</th>
                  <th className="px-4 py-3 text-left font-medium">City</th>
                  <th className="px-4 py-3 text-left font-medium">New?</th>
                  <th className="px-4 py-3 text-left font-medium">Results</th>
                  <th className="px-4 py-3 text-left font-medium">Source</th>
                  <th className="px-4 py-3 text-left font-medium">Device</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredSearches.slice(0, 100).map((search, idx) => (
                  <tr key={idx} className="hover:bg-stone-50">
                    <td className="px-4 py-3 text-stone-500 whitespace-nowrap">
                      {new Date(search.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-stone-900 max-w-[200px] truncate">
                      {search.search_query}
                    </td>
                    <td className="px-4 py-3 text-stone-600">
                      {search.visitor_city || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {search.is_new_visitor ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">New</span>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {search.results_count === 0 ? (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">0</span>
                      ) : (
                        <span className="text-green-600">{search.results_count}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-500 capitalize">
                      {search.referrer_source || 'direct'}
                    </td>
                    <td className="px-4 py-3 text-stone-500 capitalize">
                      {search.device_type || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredSearches.length === 0 && (
            <p className="px-4 py-8 text-center text-stone-500">No search events found</p>
          )}
        </div>
      )}

      {/* New User Insights Tab */}
      {activeTab === 'insights' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Queries from New Visitors */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-green-500" />
              What New Visitors Are Searching
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {insights?.top_new_visitor_queries?.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                  <span className="text-sm text-stone-700 truncate max-w-[200px]">{item.query}</span>
                  <span className="text-sm font-bold text-green-600">{item.count}</span>
                </div>
              ))}
              {!insights?.top_new_visitor_queries?.length && (
                <p className="text-stone-500 text-sm text-center py-4">No data yet</p>
              )}
            </div>
          </div>

          {/* Top Locations */}
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-500" />
              Where New Visitors Are From
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {insights?.top_locations?.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                  <span className="text-sm text-stone-700">{item.location}</span>
                  <span className="text-sm font-bold text-blue-600">{item.count}</span>
                </div>
              ))}
              {!insights?.top_locations?.length && (
                <p className="text-stone-500 text-sm text-center py-4">No location data yet</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trends & Coverage Tab */}
      {activeTab === 'trends' && (
        <div className="space-y-6">
          {/* Daily Trend Chart */}
          {insights?.daily_breakdown?.length > 0 && (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-medium text-stone-900 mb-4">Search Volume Trend</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={insights.daily_breakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#a8a29e" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#a8a29e" />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="total" name="Total Searches" stroke="#292524" strokeWidth={2} />
                    <Line type="monotone" dataKey="new_visitors" name="New Visitors" stroke="#22c55e" strokeWidth={2} />
                    <Line type="monotone" dataKey="zero_results" name="Zero Results" stroke="#ef4444" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Trending Queries */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-500" />
                Trending Up (vs Previous Period)
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {insights?.trending_queries?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-stone-700 truncate max-w-[150px]">{item.query}</span>
                      {item.is_new && (
                        <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">NEW</span>
                      )}
                    </div>
                    <span className="text-sm font-bold text-green-600">+{item.change_pct}%</span>
                  </div>
                ))}
                {!insights?.trending_queries?.length && (
                  <p className="text-stone-500 text-sm text-center py-4">Not enough data for trends</p>
                )}
              </div>
            </div>

            {/* Zero Results (Missing Coverage) */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Zero Results (Missing Coverage)
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {insights?.zero_results_queries?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                    <span className="text-sm text-stone-700 truncate max-w-[200px]">{item.query}</span>
                    <span className="text-sm font-bold text-red-600">{item.count} failed</span>
                  </div>
                ))}
                {!insights?.zero_results_queries?.length && (
                  <p className="text-stone-500 text-sm text-center py-4">All searches returning results!</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchInsightsDashboard;
